import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { Icon } from '@iconify/react/dist/iconify.js';
import { EmptyState, IconBadge, SoftChip } from '../components/ui';
import { STAGE_FIELDS } from '../constants/stageFields';
import { RT } from '../theme/retentionTheme';
import CROsessionstartdate from '../../cro/myworkspace/CROleads/CROsessionstartdate';
import { saveAdmissionChecklist } from '../../../redux/features/retentionSlice';
import useDemoData from './useDemoData';

// Stage 7 · Go-live checklist (RETENTION_WORKFLOW.md §5.5 stage 7). Each item stamps its tasks.json pair once
// (PUT /retention/:id/admission-checklist) — the same ticks the CRO Leads before-demo checklist shows. The first
// session goes through the existing Session Start Date dialog, which generates the calendar. "Confirm admission"
// (the card's stage action) needs Fee details shared and sets Admission Confirmed.

const [PARENT_SIDE, TUTOR_SIDE] = STAGE_FIELDS[7].sections;

function ChecklistItem({ label, done, disabled, onTick }) {
  return (
    <Stack direction="row" alignItems="center" gap={1.25} sx={{ py: 0.9 }}>
      <Icon
        icon={done ? 'solar:check-circle-bold' : 'solar:record-circle-linear'}
        width={20}
        color={done ? RT.success : RT.faint}
      />
      <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, color: done ? RT.ink : RT.ink2 }}>
        {label}
      </Typography>
      {done ? (
        <Typography variant="caption" sx={{ color: RT.muted }}>
          {[done.by, dayjs(done.at).format('DD MMM')].filter(Boolean).join(' · ')}
        </Typography>
      ) : (
        <Button size="small" variant="outlined" disabled={disabled} onClick={onTick}>
          Mark done
        </Button>
      )}
    </Stack>
  );
}

function Group({ section, checklist, saving, onTick }) {
  const done = section.fields.filter((f) => checklist[f.key]).length;
  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
        <IconBadge icon={section.icon} size={28} />
        <Typography variant="overline" sx={{ color: RT.ink2 }}>
          {section.title}
        </Typography>
        <SoftChip label={`${done}/${section.fields.length}`} color={done === section.fields.length ? RT.success : RT.muted} />
        <Box sx={{ flex: 1, borderTop: `1px dashed ${RT.line}` }} />
      </Stack>
      <Stack divider={<Box sx={{ borderTop: `1px dashed ${RT.line}` }} />}>
        {section.fields.map((f) => (
          <ChecklistItem key={f.key} label={f.label} done={checklist[f.key]} disabled={saving} onTick={() => onTick(f.key)} />
        ))}
      </Stack>
    </Box>
  );
}

export default function GoLiveStage() {
  const { d, demo, saving, run, courseEnrollmentId } = useDemoData();

  if (demo.error) return <Alert severity="info">{demo.error.message}</Alert>;
  if (!d) return <EmptyState icon="svg-spinners:ring-resize" title="Loading…" compact />;

  const tick = (key) => run(saveAdmissionChecklist({ courseEnrollmentId, [key]: true }), 'Marked done');
  const checklist = d.checklist || {};

  return (
    <Stack gap={2.5}>
      <Group section={PARENT_SIDE} checklist={checklist} saving={saving} onTick={tick} />
      <Group section={TUTOR_SIDE} checklist={checklist} saving={saving} onTick={tick} />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        gap={1.25}
        sx={{ p: 1.5, borderRadius: 3, bgcolor: RT.surface }}
      >
        <IconBadge icon="solar:calendar-mark-bold-duotone" size={36} color={RT.info} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2">First session</Typography>
          <Typography variant="caption" sx={{ color: RT.muted }}>
            {d.first_session_date ? dayjs(d.first_session_date).format('ddd, DD MMM YYYY') : 'Not set'}
          </Typography>
        </Box>
        <CROsessionstartdate
          courseenrollmentId={courseEnrollmentId}
          onSaved={() => {
            // mirror the earliest start date to cro_assignment.first_session_date
            run(saveAdmissionChecklist({ courseEnrollmentId }), 'First session saved');
          }}
          renderTrigger={(open) => (
            <Button size="small" variant="outlined" startIcon={<Icon icon="solar:calendar-add-bold" width={16} />} onClick={open}>
              {d.first_session_date ? 'Change' : 'Set first session'}
            </Button>
          )}
        />
      </Stack>
      {!checklist.fee_details_shared && (
        <Typography variant="caption" sx={{ color: RT.warning, fontWeight: 700 }}>
          Mark “Fee details shared” to confirm admission.
        </Typography>
      )}
    </Stack>
  );
}
